import * as ngCommon from '@angular/common'
import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'

import * as state from '../../../../../../../../state'
import * as modals from '../../../modals'

/**
 * Shared space occupants component for presenting occupants and payroll entries.
 * Works for both record and cluster contexts.
 */
@ngCore.Component({
  imports: [
    ds.button.Component,
    ds.illustration.emptyState.Component,
    ds.layout.widget.Component,
    ds.loader.Component,
    ds.overlay.confirmation.Component,
    ds.notice.Component,
    ds.notice.icon.Component,
    modals.occupants.Component,
    modals.payrolls.Component,
    modals.shared.createEmployeeForm.Component,
    ngCommon.PercentPipe,
    ngCommon.NgTemplateOutlet,
  ],
  selector: 'atn-space-occupants',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** When true, the component is in read-only mode (e.g. record assigned to a cluster). */
  public readonly isReadonlySg = ngCore.input(false, { alias: 'readonly' })

  /** The occupants entity collection */
  public readonly ecSg = ngCore.input.required<
    | state.space.full.recordOccupants.Collection
    | state.space.full.clusterOccupants.Collection
  >({ alias: 'collection' })

  /** The warning plugin for occupants   */
  public readonly warningPluginSg =
    ngCore.input.required<state.space.full.warnings.plugin.Plugin>({
      alias: 'warningPlugin',
    })

  /** Combined loading state for occupants */
  public readonly isOccupantsLoadingSg = ngCore.computed(
    (
      isOccupantsLoading = this.ecSg().plugins.currentListPlugin.feature.selectors.isLoading.asSignal(),
      isWarningsLoading = this.warningPluginSg()?.selectors.isLoading.asSignal() ??
        false,
    ) => isOccupantsLoading || isWarningsLoading,
  )

  /** Whether any occupant has a payroll only some of whose entries are assigned */
  public readonly hasPartiallyAssignedPayrollsSg = ngCore.computed(
    (
      occupants = this.ecSg().plugins.currentListPlugin.feature.selectors.entities.asSignal(),
    ) =>
      occupants.some(({ payrolls }) =>
        payrolls.some(
          ({ assignment }) =>
            assignment ===
            ents.extra.space.Record.Occupant.Payroll.Assignment.Partial,
        ),
      ),
  )

  /** @see {@link ents.extra.space.Warning.Code.MissingSponsoredAccountsForSponsoredFunctionalUsages} */
  public readonly sponsoredAccountsWarningSg = ngCore.computed(() =>
    this.warningPluginSg()
      ?.selectors.getMessage(
        ents.extra.space.Warning.Code
          .MissingSponsoredAccountsForSponsoredFunctionalUsages,
      )
      .asSignal(),
  )

  /** @see {@link state.space.full.employee.Collection} */
  public readonly employeesEc = ngCore.inject(
    state.space.full.employee.Collection,
  )

  /** @see {@link state.space.full.jobTitles.Collection} */
  public readonly jobTitlesEc = ngCore.inject(
    state.space.full.jobTitles.Collection,
  )

  /** @see {@link ents.core.display.Style} */
  public readonly DisplayStyle = ents.core.display.Style

  /** @see {@link ents.extra.space.Record.Occupant.Payroll.Assignment} */
  public readonly Assignment =
    ents.extra.space.Record.Occupant.Payroll.Assignment

  /**
   * @see {@link ngCommon.PercentPipe}
   */
  private readonly _percentPipe = ngCore.inject(ngCommon.PercentPipe)

  /** Handle edit occupant */
  public editOccupantClickHandler(
    employeeId: ents.extra.space.Employee.Id,
  ): void {
    this.ecSg()
      .plugins.editingPlugin.feature.actions.edit.from('Occupants.Edit.Clicked')
      .dispatch({ id: employeeId })
  }

  /** Handle remove occupant */
  public removeOccupantClickHandler(
    employeeId: ents.extra.space.Employee.Id,
  ): void {
    this.ecSg()
      .plugins.editingPlugin.feature.actions.markForDeletion.from(
        'Occupants.Remove.Clicked',
      )
      .dispatch({ id: employeeId })
  }

  /** Confirm occupant removal */
  public confirmRemoveOccupantClickHandler(): void {
    this.ecSg()
      .plugins.editingPlugin.feature.actions.submit.from(
        'Occupants.Delete.Confirm.Clicked',
      )
      .dispatch({ delete: true })
  }

  /** Format IDC allocations for display */
  public formatAllocations(
    idcAllocations: state.space.full.recordOccupants.Entity.Payroll.IdcAllocation[],
  ): string {
    return idcAllocations
      .map(({ idcCategorization: { category, subcategory }, percent }) => {
        const label = subcategory
          ? `${category.code} (${subcategory.code})`
          : category.code
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        return `${label}: ${this._percentPipe.transform(percent)}`
      })
      .join('; ')
  }

  /** Discard occupant removal */
  public cancelRemoveOccupantClickHandler(): void {
    this.ecSg()
      .plugins.editingPlugin.feature.actions.discard.from(
        'Occupants.Delete.Cancel.Clicked',
      )
      .dispatch({ delete: true })
  }

  /** Reload the occupants list */
  public refreshClickHandler(): void {
    this.ecSg()
      .plugins.currentListPlugin.feature.actions.load.from(
        'Occupants.Refresh.Clicked',
      )
      .dispatch()
  }

  /** Handle add occupant */
  public addOccupantClickHandler(): void {
    this.ecSg()
      .plugins.editingPlugin.feature.actions.edit.from('Occupants.Add.Clicked')
      .dispatch({
        id: ec.plugins.standard.editing.models.CreationId.generate(),
      })
  }
}
